<template>
	<div class="event-center-navigation">
		<MobileFilter
			v-if="hasMobileFilters"
			ref="mobileFilter"
			:filters="mobileFilters"
			@changeSelect="handleMobileFilterChange"
			@reset="resetMobileFilters"
		/>

		<div
			v-if="hasMobileFilters"
			class="event-center-navigation__mobile-filter-toggle"
			:class="{'is-filtered': mobileFilterIsFiltered}"
			@click="openMobileFilter"
		>
			<Icon
				icon-name="filter"
				size="lg"
			/>
		</div>

		<div
			v-if="showScopeTabs"
			class="event-center-navigation__row -scope"
		>
			<InPageNavigation
				:nav-items="resolvedScopeNavItems"
				:use-anchor-tag="false"
				:with-line="false"
				:with-margin-bottom="false"
				:line-column-width="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				horizontal
				@nav-change="$emit('scope-change', $event)"
			/>
		</div>

		<div
			v-if="showMobileCompetitionSelect"
			class="event-center-navigation__row -competition-mobile"
		>
			<Select
				class="event-center-navigation__competition-select -mobile"
				:options="resolvedCompetitionOptions"
				:value="selectedCompetitionValue"
				:searchable="false"
				:clearable="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				:rounded="true"
				@change="$emit('competition-change', $event)"
			/>
		</div>

		<div
			v-if="isCompetitionView"
			class="event-center-navigation__row -competition"
		>
			<InPageNavigation
				:nav-items="resolvedCompetitionContentNavItems"
				:use-anchor-tag="false"
				:with-line="false"
				:with-margin-bottom="false"
				:line-column-width="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				size="sm"
				horizontal
				@nav-change="$emit('competition-content-change', $event)"
			/>

			<Select
				class="event-center-navigation__competition-select"
				:options="resolvedCompetitionOptions"
				:value="selectedCompetitionValue"
				:searchable="false"
				:clearable="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				:rounded="true"
				@change="$emit('competition-change', $event)"
			/>
		</div>

		<div
			v-if="showViewNavigationRow"
			class="event-center-navigation__row -view"
		>
			<div class="event-center-navigation__view-navigation">
				<ViewSelect
					v-if="isCompetitionView"
					class="event-center-navigation__view-select"
					:options="competitionSubviewOptions"
					:value="selectedCompetitionSubviewValue"
					@change="$emit('competition-subview-change', $event)"
				/>

				<ViewSelect
					v-else
					class="event-center-navigation__view-select"
					:options="allEventsOptions"
					:value="selectedAllEventsValue"
					@change="$emit('all-events-view-change', $event)"
				/>
			</div>

			<Select
				v-if="shouldShowSubStageSelect"
				class="event-center-navigation__substage-select"
				:options="resolvedSubStageOptions"
				:value="resolvedSubStageValue"
				:searchable="false"
				:clearable="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				:rounded="true"
				@change="$emit('substage-change', $event)"
			/>
		</div>

		<div
			v-if="showFiltersRow"
			class="event-center-navigation__row -filters"
		>
			<div class="event-center-navigation__filters-desktop">
				<div
					v-if="showSelectFilters && resolvedActiveFilters.length > 0"
					class="event-center-navigation__filters-grid"
					:style="{ '--search-span': inlineSearchSpan }"
				>
					<Select
						v-for="filter in resolvedActiveFilters"
						:key="filter.key"
						class="event-center-navigation__filter-select"
						:options="filter.options"
						:value="queryState[filter.key]"
						:searchable="false"
						:clearable="false"
						:dark-theme="!$arenaConfig.isLightTheme"
						:rounded="true"
						@change="$emit('filter-change', { key: filter.key, value: $event })"
					/>

					<InputText
						v-if="shouldInlineSearch"
						class="event-center-navigation__search -inline"
						:dark-theme="!$arenaConfig.isLightTheme"
						type="search"
						:placeholder="$Helpers.ucfirst($I18n.trans('commons.search'))"
						:value="localSearchValue"
						:rounded="true"
						@input="handleSearchInput"
					/>
				</div>

				<InputText
					v-if="resolvedShowSearch && !shouldInlineSearch"
					class="event-center-navigation__search -full"
					:dark-theme="!$arenaConfig.isLightTheme"
					type="search"
					:placeholder="$Helpers.ucfirst($I18n.trans('commons.search'))"
					:value="localSearchValue"
					:rounded="true"
					@input="handleSearchInput"
				/>
			</div>

			<InputText
				v-if="resolvedShowSearch"
				class="event-center-navigation__search -full u-hide@md"
				:dark-theme="!$arenaConfig.isLightTheme"
				type="search"
				:placeholder="$Helpers.ucfirst($I18n.trans('commons.search'))"
				:value="localSearchValue"
				:rounded="true"
				@input="handleSearchInput"
			/>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon';
import MobileFilter from '@js/components/MobileFilter';
import InPageNavigation from '@shared/components/InPageNavigation';
import InputText from '@shared/components/Form/InputText';
import Select from '@shared/components/Select';
import ViewSelect from '@js/components/ViewSelect';
import { debounce } from '@shared/utils/debounce';

export default {
	name:       'EventCenterNavigation',
	components: {
		Icon,
		InPageNavigation,
		InputText,
		MobileFilter,
		Select,
		ViewSelect,
	},
	props: {
		showScopeTabs: {
			type:    Boolean,
			default: false,
		},
		navigationConfig: {
			type:    Object,
			default: null,
		},
		isCompetitionView: {
			type:    Boolean,
			default: false,
		},
		showSearch: {
			type:    Boolean,
			default: true,
		},
		showSelectFilters: {
			type:    Boolean,
			default: true,
		},
		queryState: {
			type:     Object,
			required: true,
		},
		scopeNavItems: {
			type:    Array,
			default: () => [],
		},
		allEventsNavItems: {
			type:    Array,
			default: () => [],
		},
		competitionOptions: {
			type:    Array,
			default: () => [],
		},
		competitionContentNavItems: {
			type:    Array,
			default: () => [],
		},
		subStageOptions: {
			type:    Array,
			default: () => [],
		},
		subStageValue: {
			type:    [String, Number, null],
			default: null,
		},
		activeFilters: {
			type:    Array,
			default: () => [],
		},
	},
	emits: [
		'scope-change',
		'competition-change',
		'competition-subview-change',
		'competition-content-change',
		'substage-change',
		'all-events-view-change',
		'search-change',
		'filter-change',
	],
	data() {
		return {
			debouncedSearchChange: null,
			localSearchValue:      this.queryState.q || '',
		};
	},
	computed: {
		selectedScope() {
			return this.queryState.scope || (this.isCompetitionView ? 'competitions' : 'all-events');
		},
		resolvedScopeNavItems() {
			if (!this.navigationConfig?.scopeTabs?.length) {
				return this.scopeNavItems;
			}

			return this.navigationConfig.scopeTabs
				.filter(item => item.enabled !== false)
				.map(item => ({
					label:  item.label,
					url:    item.value,
					active: item.value === this.selectedScope,
				}));
		},
		resolvedAllEventsNavItems() {
			if (!this.navigationConfig?.allEvents?.views?.length) {
				return this.allEventsNavItems;
			}

			return this.navigationConfig.allEvents.views
				.filter(item => item.enabled !== false)
				.map(item => ({
					label:  item.label,
					url:    item.value,
					active: item.value === this.queryState.view,
				}));
		},
		allEventsOptions() {
			return this.resolvedAllEventsNavItems.map(item => ({
				label: item.label,
				value: item.url,
			}));
		},
		selectedAllEventsValue() {
			return this.queryState.view || this.allEventsOptions[0]?.value || null;
		},
		resolvedCompetitionOptions() {
			const options = this.navigationConfig?.competitions?.options || this.competitionOptions;
			return options.filter(option => option.enabled !== false);
		},
		selectedCompetitionValue() {
			return this.queryState.competition || this.resolvedCompetitionOptions[0]?.value || null;
		},
		selectedCompetitionConfig() {
			return this.navigationConfig?.competitions?.byCompetitionId?.[this.selectedCompetitionValue] || null;
		},
		resolvedCompetitionContentNavItems() {
			return this.competitionContentNavItems.filter(item => item.enabled !== false);
		},
		resolvedCompetitionSubviewNavItems() {
			if (!this.selectedCompetitionConfig?.subviews?.length) {
				return [];
			}

			const selectedSubview = this.queryState.subview || this.selectedCompetitionConfig.subviews[0]?.value || null;

			return this.selectedCompetitionConfig.subviews
				.filter(item => item.enabled !== false)
				.map(item => ({
					label:  item.label,
					url:    item.value,
					active: item.value === selectedSubview,
				}));
		},
		competitionSubviewOptions() {
			return this.resolvedCompetitionSubviewNavItems.map(item => ({
				label: item.label,
				value: item.url,
			}));
		},
		selectedCompetitionSubview() {
			return this.queryState.subview || this.resolvedCompetitionSubviewNavItems.find(item => item.active)?.url || this.resolvedCompetitionSubviewNavItems[0]?.url || null;
		},
		selectedCompetitionSubviewValue() {
			return this.selectedCompetitionSubview || this.competitionSubviewOptions[0]?.value || null;
		},
		selectedCompetitionContentValue() {
			return this.resolvedCompetitionContentNavItems.find(item => item.active)?.url || 'fixtures-results';
		},
		isCompetitionFeatureViewActive() {
			return this.isCompetitionView && this.selectedCompetitionContentValue !== 'fixtures-results';
		},
		resolvedSubStageOptions() {
			if (!this.selectedCompetitionConfig?.subStagesBySubview) {
				return this.subStageOptions;
			}

			return this.selectedCompetitionConfig.subStagesBySubview[this.selectedCompetitionSubview] || [];
		},
		resolvedSubStageValue() {
			return this.queryState.substage ?? this.subStageValue ?? this.resolvedSubStageOptions[0]?.value ?? null;
		},
		resolvedActiveFilters() {
			const filters = this.isCompetitionView
				? this.selectedCompetitionConfig?.filters || []
				: this.navigationConfig?.allEvents?.filters || [];

			const mappedFilters = filters
				.filter(filter => filter.enabled !== false && filter.control === 'select')
				.map(filter => ({
					key:     filter.key,
					options: filter.options || [],
				}))
				.filter(filter => Boolean(filter.key));

			return mappedFilters.length > 0 ? mappedFilters : this.activeFilters;
		},
		resolvedShowSearch() {
			const filters = this.isCompetitionView
				? this.selectedCompetitionConfig?.filters || []
				: this.navigationConfig?.allEvents?.filters || [];

			if (filters.length === 0) {
				return this.showSearch;
			}

			const searchFilter = filters.find(filter => filter.control === 'search');
			return this.showSearch && searchFilter?.enabled !== false;
		},
		showViewNavigationRow() {
			if (this.isCompetitionFeatureViewActive) {
				return false;
			}

			if (this.isCompetitionView) {
				return this.competitionSubviewOptions.length > 1 || this.shouldShowSubStageSelect;
			}

			return this.allEventsOptions.length > 1;
		},
		shouldShowSubStageSelect() {
			return this.isCompetitionView && this.resolvedSubStageOptions.length > 0;
		},
		showMobileCompetitionSelect() {
			return this.isCompetitionView && this.resolvedCompetitionOptions.length > 1;
		},
		showFiltersRow() {
			if (this.isCompetitionFeatureViewActive) {
				return false;
			}

			const hasSelectFilters = this.showSelectFilters && this.resolvedActiveFilters.length > 0;
			return hasSelectFilters || this.resolvedShowSearch;
		},
		mobileSubStageFilter() {
			if (!this.shouldShowSubStageSelect) {
				return null;
			}

			return {
				type:         'select',
				id:           'substage',
				label:        this.$I18n.trans_choice('commons.sub_stage', 1),
				defaultValue: this.resolvedSubStageOptions[0]?.value ?? null,
				value:        this.resolvedSubStageValue,
				options:      this.resolvedSubStageOptions,
			};
		},
		mobileFilters() {
			const filters = this.showSelectFilters
				? this.resolvedActiveFilters.map(filter => ({
					type:         'select',
					id:           filter.key,
					label:        filter.options?.[0]?.label || filter.key,
					defaultValue: filter.options?.[0]?.value ?? null,
					value:        this.queryState[filter.key] ?? filter.options?.[0]?.value ?? null,
					options:      filter.options || [],
				}))
				: [];

			return [
				...(this.mobileSubStageFilter ? [this.mobileSubStageFilter] : []),
				...filters,
			];
		},
		hasMobileFilters() {
			return this.mobileFilters.length > 0;
		},
		mobileFilterIsFiltered() {
			return this.mobileFilters.some(filter => filter.value !== filter.defaultValue);
		},
		inlineSearchSpan() {
			const remainder = this.resolvedActiveFilters.length % 4;
			if (remainder === 0) {
				return 1;
			}

			return 4 - remainder;
		},
		shouldInlineSearch() {
			if (!this.resolvedShowSearch || !this.showSelectFilters) {
				return false;
			}

			const activeFilterCount = this.resolvedActiveFilters.length;
			if (activeFilterCount <= 0) {
				return false;
			}

			return activeFilterCount % 4 !== 0;
		},
	},
	watch: {
		'queryState.q'(value) {
			this.localSearchValue = value || '';
		},
	},
	created() {
		// Debounce query updates so typing does not re-run filtering and URL state on every keypress.
		this.debouncedSearchChange = debounce(this.emitSearchChange, 300, false);
	},
	methods: {
		handleSearchInput(valueOrEvent) {
			const nextValue = typeof valueOrEvent === 'string'
				? valueOrEvent
				: valueOrEvent?.target?.value || '';

			this.localSearchValue = nextValue;
			this.debouncedSearchChange(nextValue);
		},
		emitSearchChange(value) {
			this.$emit('search-change', value);
		},
		openMobileFilter() {
			this.$refs.mobileFilter?.open();
		},
		handleMobileFilterChange(select, value) {
			const key = select?.id;
			if (!key) {
				return;
			}

			if (key === 'substage') {
				this.$emit('substage-change', value);
				return;
			}

			this.$emit('filter-change', { key, value });
		},
		resetMobileFilters() {
			this.mobileFilters.forEach(filter => {
				if (filter.id === 'substage') {
					this.$emit('substage-change', filter.defaultValue);
					return;
				}

				this.$emit('filter-change', {
					key:   filter.id,
					value: filter.defaultValue,
				});
			});
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.event-center-navigation {
	position: relative;
}

.event-center-navigation__row {
	display: flex;
	align-items: center;
	gap: $sp2;
	margin-bottom: $sp4-5;

	&.-scope {
		margin-bottom: $sp5;
	}

	&.-competition {
		justify-content: space-between;
	}

	&.-competition-mobile {
		@include size(md-up) {
			display: none;
		}
	}

	&.-view {
		display: flex;
		flex-wrap: wrap;
		gap: $sp4;
		justify-content: space-between;

		@include size(md-up) {
			min-height: 52px;
		}
	}

	&.-filters {
		display: block;

		@include size(md-up) {
			margin-bottom: $sp6;
		}
	}

	@include size(md-up) {
		margin-bottom: $sp3-5;
	}
}

.event-center-navigation__competition-select,
.event-center-navigation__substage-select {
	display: none;
	width: 100%;
	flex: 1 1 auto;
	margin-bottom: 0;

	@include size(md-up) {
		display: block;
		width: 300px;
		flex: 0 0 300px;
	}
}

.event-center-navigation__competition-select.-mobile {
	display: block;

	@include size(md-up) {
		display: none;
	}
}

.event-center-navigation__view-navigation {
	display: flex;
	align-items: center;
	gap: $sp2;
	flex: 1 1 auto;
	min-width: 0;
}

.event-center-navigation__view-select {
	flex: 1 1 auto;
	min-width: 0;
	margin-bottom: 0;
}

.event-center-navigation__filters-desktop {
	display: none;

	@include size(md-up) {
		display: block;
	}
}

.event-center-navigation__filters-grid {
	display: none;

	@include size(md-up) {
		display: grid;
		gap: $sp2;
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}
}

.event-center-navigation__search {
	&.-inline {
		grid-column: span var(--search-span);
		min-width: 0;
	}

	&.-full {
		width: 100%;
	}
}

.event-center-navigation__mobile-filter-toggle {
	position: absolute;
	right: 0;
	top: $sp2;
	display: flex;
	align-items: center;
	cursor: pointer;

	&.is-filtered {
		color: var(--color-hype-yellow);
	}

	@include size(md-up) {
		display: none;
	}
}

@include size(sm-only) {
	.event-center-navigation__search.-inline {
		grid-column: span 1;
	}
}
</style>
